// Real musical transposition engine (not text replacement).

const SHARP = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

export const KEYS = ["C", "C#", "Db", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];
const FLAT_KEYS = new Set(["F", "Bb", "Eb", "Ab", "Db", "Gb", "Cb", "Dm", "Gm", "Cm", "Fm"]);

const BASE = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

function noteToIndex(n) {
  if (!n) return null;
  const letter = n[0].toUpperCase();
  if (!(letter in BASE)) return null;
  let idx = BASE[letter];
  for (const c of n.slice(1)) {
    if (c === "#") idx += 1;
    else if (c === "b") idx -= 1;
    else break;
  }
  return ((idx % 12) + 12) % 12;
}

function indexToNote(idx, preferFlats) {
  idx = ((idx % 12) + 12) % 12;
  return preferFlats ? FLAT[idx] : SHARP[idx];
}

// Parse a single chord token -> transpose -> rebuild. Handles slash chords & suffixes.
export function transposeChord(token, steps, preferFlats) {
  if (!token) return token;
  const m = token.match(/^([A-G][b#]?)(.*)$/);
  if (!m) return token;
  const [, root, rest] = m;
  const rootIdx = noteToIndex(root);
  if (rootIdx === null) return token;
  let suffix = rest;
  let bass = "";
  const slash = rest.indexOf("/");
  if (slash !== -1) {
    suffix = rest.slice(0, slash);
    const bassPart = rest.slice(slash + 1);
    const bm = bassPart.match(/^([A-G][b#]?)(.*)$/);
    if (bm) {
      const bIdx = noteToIndex(bm[1]);
      bass = "/" + indexToNote(bIdx + steps, preferFlats) + (bm[2] || "");
    } else {
      bass = "/" + bassPart;
    }
  }
  return indexToNote(rootIdx + steps, preferFlats) + suffix + bass;
}

// Transpose a whitespace-preserving chord line (e.g. "E     A    C#m").
export function transposeChordLine(line, steps, preferFlats) {
  if (!line) return line;
  return line.replace(/([A-G][b#]?[^\s]*)/g, (tok) => transposeChord(tok, steps, preferFlats));
}

export function stepsBetween(fromKey, toKey) {
  const a = noteToIndex(fromKey);
  const b = noteToIndex(toKey);
  if (a === null || b === null) return 0;
  return ((b - a) % 12 + 12) % 12;
}

export function transposeKey(key, steps) {
  const idx = noteToIndex(key);
  if (idx === null) return key;
  const minor = key.endsWith("m") && !key.endsWith("maj");
  const flats = shouldUseFlats(transposeKeyName(idx + steps));
  return indexToNote(idx + steps, flats) + (minor ? "m" : "");
}

function transposeKeyName(idx) {
  return indexToNote(idx, false);
}

export function shouldUseFlats(targetKey) {
  if (!targetKey) return false;
  if (targetKey.includes("b")) return true;
  return FLAT_KEYS.has(targetKey);
}
